import { motion } from 'framer-motion';

import { cn } from '@llura/ui';

interface GlowLogoProps {
  size?: number;
  className?: string;
  pulse?: boolean;
  /** `rose` is the signature plum/rose-gold; `emerald` matches the cocoon / safe-word palette. */
  tone?: 'rose' | 'emerald';
}

/**
 * llura signature mark — a sensual rose-petal / flame silhouette that pulses
 * with a plum + rose-gold aura. Used on Welcome, splash, and the Profile coach card.
 */
export function GlowLogo({ size = 128, className, pulse = true, tone = 'rose' }: GlowLogoProps) {
  const emerald = tone === 'emerald';
  // Unique gradient ids so multiple GlowLogo instances on the same page don't bleed colours.
  const gradId = emerald ? 'lluraGradEmerald' : 'lluraGrad';
  const coreId = emerald ? 'lluraCoreEmerald' : 'lluraCore';
  return (
    <div className={cn('relative inline-flex', className)} style={{ width: size, height: size }}>
      {pulse && (
        <>
          <motion.div
            aria-hidden
            className={cn(
              'absolute inset-0 rounded-full blur-2xl',
              emerald ? 'bg-emerald-500/30' : 'bg-primary/30',
            )}
            animate={{ scale: [1, 1.25, 1], opacity: [0.55, 0.85, 0.55] }}
            transition={{ duration: 3.2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.div
            aria-hidden
            className={cn(
              'absolute inset-0 rounded-full blur-3xl',
              emerald ? 'bg-emerald-400/25' : 'bg-secondary/25',
            )}
            animate={{ scale: [1.05, 1.35, 1.05], opacity: [0.4, 0.7, 0.4] }}
            transition={{ duration: 4.2, repeat: Infinity, ease: 'easeInOut', delay: 0.6 }}
          />
        </>
      )}

      <motion.svg
        viewBox="0 0 100 100"
        width={size}
        height={size}
        className={cn(
          'relative',
          emerald
            ? 'drop-shadow-[0_0_18px_rgba(52,211,153,0.65)]'
            : 'drop-shadow-[0_0_18px_hsl(var(--primary)/0.65)]',
        )}
        animate={pulse ? { rotate: [0, 4, -4, 0] } : undefined}
        transition={pulse ? { duration: 6, repeat: Infinity, ease: 'easeInOut' } : undefined}
      >
        <defs>
          <linearGradient id={gradId} x1="0" y1="0" x2="1" y2="1">
            {emerald ? (
              <>
                <stop offset="0%" stopColor="hsl(152 76% 70%)" />
                <stop offset="60%" stopColor="hsl(160 84% 39%)" />
                <stop offset="100%" stopColor="hsl(163 80% 22%)" />
              </>
            ) : (
              <>
                <stop offset="0%" stopColor="hsl(18 55% 70%)" />
                <stop offset="60%" stopColor="hsl(325 38% 38%)" />
                <stop offset="100%" stopColor="hsl(315 35% 25%)" />
              </>
            )}
          </linearGradient>
          <radialGradient id={coreId} cx="50%" cy="55%" r="50%">
            {emerald ? (
              <>
                <stop offset="0%" stopColor="hsl(152 50% 86%)" stopOpacity="0.95" />
                <stop offset="80%" stopColor="hsl(160 84% 39%)" stopOpacity="0" />
              </>
            ) : (
              <>
                <stop offset="0%" stopColor="hsl(38 38% 78%)" stopOpacity="0.95" />
                <stop offset="80%" stopColor="hsl(325 38% 38%)" stopOpacity="0" />
              </>
            )}
          </radialGradient>
        </defs>

        {/* outer flame / petal pair */}
        <path
          d="M50 8 C 70 28, 86 44, 78 66 C 72 84, 56 92, 50 92 C 44 92, 28 84, 22 66 C 14 44, 30 28, 50 8 Z"
          fill={`url(#${gradId})`}
        />
        {/* inner mirror highlight */}
        <path
          d="M50 22 C 62 38, 72 50, 66 64 C 62 76, 54 82, 50 82 C 46 82, 38 76, 34 64 C 28 50, 38 38, 50 22 Z"
          fill={`url(#${coreId})`}
        />
        {/* spine */}
        <path
          d="M50 16 L50 88"
          stroke="hsl(0 0% 100% / 0.55)"
          strokeWidth="1.2"
          strokeLinecap="round"
        />
      </motion.svg>
    </div>
  );
}
